import type { ComponentChildren, JSX } from 'preact'
import { useQuery } from '@tanstack/react-query'
import { listSites } from '../api/endpoints'
import { registerSites, siteMeta } from '../utils/site'
import type { SessionStatus, SiteId } from '../types'

// ── Button ──────────────────────────────────────────────────────────────────
type ButtonProps = Omit<JSX.IntrinsicElements['button'], 'class'> & {
  variant?: 'primary' | 'accent' | 'outline' | 'ghost' | 'danger'
  size?: 'md' | 'lg'
  block?: boolean
  loading?: boolean
  class?: string
}

export function Button({
  variant = 'primary',
  size = 'md',
  block,
  loading,
  disabled,
  class: className,
  children,
  ...rest
}: ButtonProps) {
  const classes = [
    'btn',
    `btn-${variant}`,
    size === 'lg' ? 'btn-lg' : '',
    block ? 'btn-block' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ')
  return (
    <button
      class={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading && <span class="spinner" aria-hidden="true" />}
      {children}
    </button>
  )
}

// ── Card ────────────────────────────────────────────────────────────────────
export function Card({
  children,
  class: className,
  style,
}: {
  children: ComponentChildren
  class?: string
  // Inline style for one-off margin/padding overrides — used widely by the
  // page modules and would have to be plumbed through individually otherwise.
  style?: JSX.CSSProperties
}) {
  return <div class={`card ${className ?? ''}`} style={style}>{children}</div>
}

// ── Badge ───────────────────────────────────────────────────────────────────
type BadgeVariant = 'neutral' | 'primary' | 'accent' | 'success' | 'warn' | 'error'
export function Badge({
  variant = 'neutral',
  children,
  dot,
}: {
  variant?: BadgeVariant
  children: ComponentChildren
  dot?: boolean
}) {
  return (
    <span class={`badge badge-${variant}`}>
      {dot && <span class={`dot`} style={{ background: 'currentColor' }} />}
      {children}
    </span>
  )
}

// ── Status pill ─────────────────────────────────────────────────────────────
export function StatusPill({ status }: { status: SessionStatus }) {
  switch (status) {
    case 'escalated':
      return (
        <Badge variant="accent" dot>
          Needs reply
        </Badge>
      )
    case 'human':
      return (
        <Badge variant="primary" dot>
          In progress
        </Badge>
      )
    case 'timed_out':
      return (
        <Badge variant="warn" dot>
          Timed out
        </Badge>
      )
    case 'closed':
      return <Badge variant="neutral">Closed</Badge>
    case 'active':
    case 'collecting_email':
      return <Badge variant="neutral">Bot</Badge>
    default:
      return <Badge variant="neutral">{status}</Badge>
  }
}

// ── Site badge ──────────────────────────────────────────────────────────────
export function SiteBadge({ site }: { site: SiteId }) {
  // Shared cached query keeps the runtime site-name registry fresh, so
  // client sites added via the wizard show their real display name here
  // (and everywhere siteMeta is used) without a code change.
  const sitesQuery = useQuery({ queryKey: ['sites'], queryFn: listSites, staleTime: 300_000 })
  if (sitesQuery.data) registerSites(sitesQuery.data.sites)
  const meta = siteMeta(site)
  return (
    <span
      class="badge"
      style={{
        background: `${meta.accent}15`,
        color: meta.accent,
      }}
    >
      {meta.name}
    </span>
  )
}

// ── Banner ──────────────────────────────────────────────────────────────────
export function Banner({
  variant = 'info',
  children,
  style,
}: {
  variant?: 'info' | 'warn' | 'error' | 'success'
  children: ComponentChildren
  style?: JSX.CSSProperties
}) {
  return <div class={`banner banner-${variant}`} style={style}>{children}</div>
}

// ── Empty state ─────────────────────────────────────────────────────────────
export function EmptyState({
  title,
  subtitle,
  children,
}: {
  title: string
  subtitle?: string
  children?: ComponentChildren
}) {
  return (
    <div class="empty">
      <h2>{title}</h2>
      {subtitle && <p>{subtitle}</p>}
      {children}
    </div>
  )
}

// ── Switch ──────────────────────────────────────────────────────────────────
export function Switch({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  label?: string
  disabled?: boolean
}) {
  return (
    <label class="switch" aria-label={label}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) =>
          onChange((e.currentTarget as HTMLInputElement).checked)
        }
      />
      <span class="slider" />
    </label>
  )
}

// ── Logo mark ───────────────────────────────────────────────────────────────
export function LogoMark({ size = 28 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      aria-hidden="true"
      style={{ display: 'block' }}
    >
      <rect x="9" y="16" width="46" height="30" rx="7" fill="currentColor" />
      <path d="M18 46 L14 55 L28 46 Z" fill="currentColor" />
      <circle cx="21" cy="31" r="3.6" fill="var(--m-surface, #fff)" opacity="0.55" />
      <circle cx="32" cy="31" r="4.2" fill="var(--m-surface, #fff)" opacity="0.78" />
      <circle cx="43" cy="31" r="4.8" fill="var(--m-surface, #fff)" />
    </svg>
  )
}
